import type { DatabaseEngineId } from '@/core/engines';
import type { CustomSqlSnippet } from '../types/sqlSnippetTypes';
import { getBuiltInSqlSnippetTemplates } from './builtInSqlSnippetTemplates';
import { normalizeSnippetEngineId } from './sqlSnippetTemplateUtils';

export type SqlSnippetCompletion = {
  readonly id: string;
  readonly label: string;
  readonly detail: string;
  readonly documentation: string;
  readonly insertText: string;
  readonly filterText: string;
  readonly sortText: string;
};

export type SqlSnippetContext = {
  readonly engineId: DatabaseEngineId;
  readonly statementTextBeforeCursor: string;
  readonly currentWord: string;
  readonly triggerKind?: number;
  readonly triggerCharacter?: string;
  readonly customSnippets?: readonly CustomSqlSnippet[];
  /**
   * True when the cursor sits inside a routine/trigger compound body. Inside a
   * body the naive statement splitter reports an empty statement at every
   * post-`;` slot, which would otherwise flood the suggest list with the full
   * builtin-template set and bury the routine's locals + keyword list. When set,
   * the unconditional statement-start flood is suppressed; word-filtered snippet
   * matching still applies so typing a prefix (`sel…`) surfaces the template.
   */
  readonly insideProceduralBody?: boolean;
};

const SERVER_TRIGGER_CHARS = new Set(['.', ':', '\\', '[', '"']);
const MIN_WORD_LENGTH_AUTOMATIC = 2;

const isManualTrigger = (triggerKind: number | undefined): boolean => {
  // Monaco CompletionTriggerKind.Invoke = 0
  return triggerKind === 0;
};

const shouldOfferSnippets = ({
  statementTextBeforeCursor,
  currentWord,
  triggerKind,
  triggerCharacter,
}: SqlSnippetContext): boolean => {
  if (triggerCharacter && SERVER_TRIGGER_CHARS.has(triggerCharacter)) return false;

  const statementTrimmed = statementTextBeforeCursor.trimStart();
  const wordTrimmed = currentWord.trim();
  const isAtStatementStartOrFirstToken = statementTrimmed.length === 0 || /^[a-zA-Z]+$/.test(statementTrimmed);

  if (!isAtStatementStartOrFirstToken) {
    return false;
  }

  if (isManualTrigger(triggerKind)) {
    return statementTrimmed.length === 0 || wordTrimmed.length > 0;
  }

  // Avoid noise: only offer snippets when user is typing at least 2 characters.
  return wordTrimmed.length >= MIN_WORD_LENGTH_AUTOMATIC;
};

const toFilterTokens = (value: string): readonly string[] => {
  return value
    .split(/\s+/g)
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
};

const createCustomSnippetCompletion = (snippet: CustomSqlSnippet, sortIndex: number): SqlSnippetCompletion => {
  const filterText = `${snippet.prefix} ${snippet.label}`.trim();
  return {
    id: `custom:${snippet.id}`,
    label: snippet.label,
    detail: 'Custom snippet',
    documentation: snippet.description?.trim() ? snippet.description : 'Custom snippet',
    insertText: snippet.body,
    filterText,
    sortText: `0-${String(sortIndex).padStart(4, '0')}_custom_${snippet.id}`,
  };
};

const createBuiltInSnippetCompletion = (template: { readonly id: string; readonly label: string; readonly detail: string; readonly documentation: string; readonly insertText: string; readonly filterText: string }, sortIndex: number): SqlSnippetCompletion => {
  return {
    id: template.id,
    label: template.label,
    detail: template.detail,
    documentation: template.documentation,
    insertText: template.insertText,
    filterText: template.filterText,
    sortText: `0-${String(sortIndex).padStart(4, '0')}_builtin_${template.id}`,
  };
};

export function getSqlSnippetCompletions(context: SqlSnippetContext): readonly SqlSnippetCompletion[] {
  if (!shouldOfferSnippets(context)) return [];

  const engineId = normalizeSnippetEngineId(context.engineId);
  const word = context.currentWord.trim().toLowerCase();
  const statementTrimmed = context.statementTextBeforeCursor.trimStart().toLowerCase();
  const isStatementStart = statementTrimmed.length === 0;

  const custom = (context.customSnippets ?? []).filter((snippet) => snippet.engineId === engineId);
  const builtInTemplates = getBuiltInSqlSnippetTemplates(engineId);

  const combined: SqlSnippetCompletion[] = [];
  const seenLabels = new Set<string>();

  const pushUnique = (completion: SqlSnippetCompletion) => {
    const key = completion.label.trim().toLowerCase();
    if (!key) return;
    if (seenLabels.has(key)) return;
    seenLabels.add(key);
    combined.push(completion);
  };

  custom.forEach((snippet, index) => pushUnique(createCustomSnippetCompletion(snippet, index)));
  builtInTemplates.forEach((template, templateIndex) =>
    pushUnique(createBuiltInSnippetCompletion(template, custom.length + templateIndex))
  );

  if (isStatementStart && isManualTrigger(context.triggerKind) && !context.insideProceduralBody) {
    return combined;
  }

  if (!word) {
    // At statement start, showing snippets only on manual trigger keeps suggestions clean.
    return [];
  }

  return combined.filter((snippet) => toFilterTokens(snippet.filterText).some((token) => token.startsWith(word)));
}
